import { useEffect, useState } from "react";
import {
  App,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
} from "antd";
import type { UploadFile } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import errorCodes from "@/apis/error";
import {
  addRecord,
  borrow as borrowEquipment,
  doReturn as returnEquipment,
  scrap as scrapEquipment,
} from "@/apis/equipment";
import LogAttachments, {
  LogAttachmentUpload,
} from "@/components/DetailModal/components/LogHome/LogAttachments";
import {
  createLogAttachmentDocument,
} from "@/components/DetailModal/components/LogHome/LogAttachments/model";
import { CurrencySelect } from "@/components/Selector";
import {
  EquipmentRecordParams,
  EquipmentRecordTypeEnum,
} from "@/types/equipment";
import type { WFile } from "@/types/file";
import { useEquipmentEnumsContext } from "../../context/EquipmentEnumsContext";

/** 设备记录表单入参。 */
interface EquipmentRecordFormProps {
  open: boolean;
  equipmentId?: string;
  presetRecordType?: EquipmentRecordTypeEnum;
  onCancel: () => void;
  onSaved: () => void;
}

/** 设备记录表单字段。 */
interface EquipmentRecordFormValues {
  recordType: number;
  occurredAt?: Dayjs | null;
  expectedReturnAt?: Dayjs | null;
  cost?: number | null;
  costCurrency?: string | null;
  odometer?: number | null;
  vendor?: string | null;
  comment?: string | null;
}

/** 新增维护记录，或执行领用、归还和报废操作。 */
const EquipmentRecordForm = ({
  open,
  equipmentId,
  presetRecordType,
  onCancel,
  onSaved,
}: EquipmentRecordFormProps) => {
  const { t } = useTranslation();
  const { modal, notification } = App.useApp();
  const [form] = Form.useForm<EquipmentRecordFormValues>();
  // 列表专用操作固定记录类型，普通维护则读取表单选择。
  const watchedRecordType = Form.useWatch("recordType", form);
  const selectedRecordType = presetRecordType ?? watchedRecordType;
  const [saving, setSaving] = useState(false);
  // 暂存已选择及正在上传的附件。
  const [attachmentFiles, setAttachmentFiles] = useState<
    UploadFile<WFile>[]
  >([]);
  const { recordTypeItems: typeItems } = useEquipmentEnumsContext();

  // 每次打开表单时清空旧数据，并默认使用当前时间。
  useEffect(() => {
    if (!open) {
      form.resetFields();
      setAttachmentFiles([]);
      return;
    }

    form.resetFields();
    setAttachmentFiles([]);
    form.setFieldsValue({ occurredAt: dayjs() });
  }, [equipmentId, form, open, presetRecordType]);

  const attachmentUploading = attachmentFiles.some(
    (file) => file.status === "uploading",
  );

  // 根据记录类型分别调用领用、归还、报废或普通记录接口。
  const submitRecord = async (values: EquipmentRecordFormValues) => {
    if (!equipmentId) return;

    const comment = values.comment?.trim() || null;
    setSaving(true);
    try {
      if (values.recordType === EquipmentRecordTypeEnum.BORROW) {
        await borrowEquipment(equipmentId, {
          expectedReturnAt: values.expectedReturnAt?.valueOf() ?? null,
          comment,
        });
      } else if (values.recordType === EquipmentRecordTypeEnum.RETURN) {
        await returnEquipment(equipmentId, { comment });
      } else if (values.recordType === EquipmentRecordTypeEnum.SCRAP) {
        await scrapEquipment(equipmentId, { comment });
      } else {
        if (!values.occurredAt) return;

        const costCurrency = values.costCurrency
          ? Number(values.costCurrency)
          : null;
        // 只提交上传成功的文件，并先创建关联附件文档。
        const uploadedFiles = attachmentFiles.flatMap((file) =>
          file.response ? [file.response] : [],
        );
        const requestDoc = await createLogAttachmentDocument(uploadedFiles);
        const body: EquipmentRecordParams = {
          recordType: values.recordType,
          occurredAt: values.occurredAt.valueOf(),
          cost: values.cost ?? null,
          costCurrency: Number.isNaN(costCurrency) ? null : costCurrency,
          odometer: values.odometer ?? null,
          vendor: values.vendor?.trim() || null,
          comment,
          docId: requestDoc?.id ?? null,
        };
        await addRecord(equipmentId, body);
      }
      notification.success({
        message: t("equipment.addRecordSucceeded"),
      });
      onSaved();
    } catch (error) {
      notification.error({
        message: t("equipment.addRecordFailed"),
        description: errorCodes[Number(error)] ?? String(error),
      });
    } finally {
      setSaving(false);
    }
  };

  // 校验表单后提交；报废操作需再次确认。
  const handleSubmit = async () => {
    if (!equipmentId || attachmentUploading) return;

    let values: EquipmentRecordFormValues;
    try {
      const formValues = await form.validateFields();
      values = {
        ...formValues,
        recordType: presetRecordType ?? formValues.recordType,
      };
    } catch {
      return;
    }

    if (values.recordType === EquipmentRecordTypeEnum.SCRAP) {
      modal.confirm({
        title: t("equipment.scrapEquipment"),
        content: t("equipment.confirmScrapEquipment"),
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
        okButtonProps: { danger: true },
        onOk: () => submitRecord(values),
      });
      return;
    }

    await submitRecord(values);
  };

  const isBorrow = selectedRecordType === EquipmentRecordTypeEnum.BORROW;
  const isReturn = selectedRecordType === EquipmentRecordTypeEnum.RETURN;
  const isScrap = selectedRecordType === EquipmentRecordTypeEnum.SCRAP;
  // 专用操作只展示各自所需字段，其他类型展示维护记录字段。
  const showMaintenanceFields = !isBorrow && !isReturn && !isScrap;
  const maintenanceTypeItems = typeItems.filter(({ v }) =>
    v !== EquipmentRecordTypeEnum.BORROW &&
    v !== EquipmentRecordTypeEnum.RETURN &&
    v !== EquipmentRecordTypeEnum.SCRAP
  );
  const title = isBorrow
    ? t("equipment.borrowEquipment")
    : isReturn
      ? t("equipment.returnEquipment")
      : isScrap
        ? t("equipment.scrapEquipment")
        : t("equipment.addMaintenanceRecord");

  return (
    <Modal
      title={title}
      open={open}
      width={720}
      maskClosable={false}
      destroyOnClose
      confirmLoading={saving}
      okButtonProps={{ disabled: !equipmentId || attachmentUploading }}
      styles={{
        content: { padding: "16px 20px" },
        header: { marginBottom: 8 },
        footer: { marginTop: 8 },
      }}
      okText={t("common.confirm")}
      cancelText={t("common.cancel")}
      onCancel={onCancel}
      onOk={handleSubmit}
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: 8 }}
      >
        <Row gutter={12}>
          {presetRecordType === undefined && (
            <Col span={12}>
              <Form.Item
                label={t("equipment.recordType")}
                name="recordType"
                rules={[{
                  required: true,
                  message: t("equipment.recordTypeRequired"),
                }]}
                style={{ marginBottom: 12 }}
              >
                <Select
                  options={maintenanceTypeItems.map(({ v, desc }) => ({
                    value: v,
                    label: desc,
                  }))}
                />
              </Form.Item>
            </Col>
          )}
          {isBorrow && (
            <Col span={12}>
              <Form.Item
                label={t("equipment.expectedReturnAt")}
                name="expectedReturnAt"
                rules={[{ required: true }]}
                style={{ marginBottom: 12 }}
              >
                <DatePicker showTime style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          )}
          {showMaintenanceFields && (
            <>
              <Col span={12}>
                <Form.Item
                  label={t("equipment.occurredAt")}
                  name="occurredAt"
                  rules={[{
                    required: true,
                    message: t("equipment.occurredAtRequired"),
                  }]}
                  style={{ marginBottom: 12 }}
                >
                  <DatePicker showTime style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={t("equipment.recordCost")}
                  name="cost"
                  style={{ marginBottom: 12 }}
                >
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={t("equipment.recordCostCurrency")}
                  name="costCurrency"
                  style={{ marginBottom: 12 }}
                >
                  <CurrencySelect />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={t("equipment.odometer")}
                  name="odometer"
                  style={{ marginBottom: 12 }}
                >
                  <InputNumber min={0} precision={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={t("equipment.vendor")}
                  name="vendor"
                  style={{ marginBottom: 12 }}
                >
                  <Input />
                </Form.Item>
              </Col>
            </>
          )}
          <Col span={24}>
            <Form.Item
              label={t("equipment.recordComment")}
              name="comment"
              style={{ marginBottom: showMaintenanceFields ? 12 : 0 }}
            >
              <Input.TextArea autoSize={{ minRows: 3, maxRows: 6 }} />
            </Form.Item>
          </Col>
          {showMaintenanceFields && (
            <Col span={24}>
              <Form.Item
                label={t("common.attachment")}
                style={{ marginBottom: 0 }}
              >
                <LogAttachmentUpload
                  fileList={attachmentFiles}
                  disabled={saving}
                  onChange={setAttachmentFiles}
                />
                <LogAttachments
                  files={attachmentFiles}
                  disabled={saving}
                  onRemove={(uid) =>
                    setAttachmentFiles((files) =>
                      files.filter((file) => file.uid !== uid),
                    )
                  }
                />
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>
    </Modal>
  );
};

export default EquipmentRecordForm;
